<template>
    <div class="role-edit-container animated fadeIn">
        <!-- Loading Skeleton -->
        <div v-if="loading" class="loading-state">
            <b-card class="role-basic-info mb-4">
                <b-skeleton animation="throb" width="30%" height="24px" class="mb-4"></b-skeleton>
                <b-row>
                    <b-col md="6">
                        <b-skeleton animation="throb" width="20%" height="16px" class="mb-2"></b-skeleton>
                        <b-skeleton animation="throb" height="46px" class="mb-3"></b-skeleton>
                    </b-col>
                    <b-col md="6">
                        <b-skeleton animation="throb" width="20%" height="16px" class="mb-2"></b-skeleton>
                        <b-skeleton animation="throb" height="46px" class="mb-3"></b-skeleton>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="6">
                        <b-skeleton animation="throb" width="25%" height="16px" class="mb-2"></b-skeleton>
                        <b-skeleton animation="throb" height="38px" class="mb-3"></b-skeleton>
                    </b-col>
                    <b-col md="6">
                        <b-skeleton animation="throb" width="15%" height="16px" class="mb-2"></b-skeleton>
                        <b-skeleton animation="throb" height="38px" class="mb-3"></b-skeleton>
                    </b-col>
                </b-row>
            </b-card>

            <b-card class="permissions-card">
                <div class="d-flex justify-content-between align-items-center mb-4">
                    <b-skeleton animation="throb" width="20%" height="24px"></b-skeleton>
                    <b-skeleton animation="throb" width="15%" height="32px"></b-skeleton>
                </div>
                <b-skeleton animation="throb" height="46px" class="mb-4"></b-skeleton>
                <div class="components-grid">
                    <div v-for="i in 6" :key="i" class="component-card-skeleton">
                        <b-skeleton animation="throb" height="60px" class="mb-2"></b-skeleton>
                        <b-skeleton animation="throb" height="120px"></b-skeleton>
                    </div>
                </div>
            </b-card>
        </div>

        <!-- Actual Content -->
        <div v-else>
            <!-- Basic Info Card -->
            <b-card class="role-basic-info mb-4">
            <h4 class="mb-4">{{ $t('roleInformation') }}</h4>
            <b-row>
                <b-col md="6">
                    <b-form-group :label="this.$t('name')" label-for="name">
                        <b-form-input id="name" type="text" v-model="name" size="lg" disabled></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group :label="this.$t('level')" label-for="level" :description="$t('roleLevelDescription')">
                        <b-form-input id="level" type="number" v-model.number="level" size="lg"></b-form-input>
                    </b-form-group>
                </b-col>
            </b-row>
            
            <b-row>
                <b-col md="6">
                    <b-form-group :label="this.$t('workspaces')" label-for="workspaces">
                        <v-select id="workspaces" v-model="spaces" :options="workspaces" :reduce="o => o.code" multiple></v-select>
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group :label="this.$t('tags')" label-for="tags">
                        <v-select id="tags" v-model="tags" :options="[]" taggable multiple push-tags :no-drop="true" :placeholder="$t('addTags')"></v-select>
                    </b-form-group>
                </b-col>
            </b-row>

            <div class="d-flex justify-content-between">
                <b-button v-if="!name.startsWith('forge::')" variant="danger" size="lg" @click="remove">
                    <b-icon icon="trash"></b-icon> {{ $t('delete') }}
                </b-button>
                <div :class="{ 'ml-auto': name.startsWith('forge::') }">
                    <b-button variant="outline-primary" size="lg" @click="showCloneModal" class="mr-2">
                        <b-icon icon="files"></b-icon> {{ $t('clone') }}
                    </b-button>
                    <b-button variant="success" size="lg" @click="save">
                        <b-icon icon="check-circle"></b-icon> {{ $t('save') }}
                    </b-button>
                </div>
            </div>
        </b-card>

        <!-- Permissions Card -->
        <b-card class="permissions-card">
            <div class="permissions-header mb-4">
                <h4>{{ $t('permissions') }}</h4>
                <div class="header-actions">
                    <b-button variant="primary" size="sm" @click="selectAllPermissions" class="mr-2">
                        <b-icon icon="check-all"></b-icon> {{ $t('selectAll') }}
                    </b-button>
                    <b-badge variant="primary" pill>{{ perms.length }} {{ $t('selected') }}</b-badge>
                </div>
            </div>

            <!-- Search -->
            <b-form-input 
                v-model="filter" 
                :placeholder="$t('searchPermissions')" 
                class="mb-4"
                size="lg"
            >
                <template #prepend>
                    <b-icon icon="search"></b-icon>
                </template>
            </b-form-input>

            <!-- Category Tabs -->
            <div class="tabs-container mb-4">
                <button 
                    class="scroll-button scroll-left" 
                    @click="scrollTabs('left')"
                    v-show="showLeftArrow"
                >
                    <b-icon icon="chevron-left"></b-icon>
                </button>
                
                <b-tabs ref="permissionsTabs" v-model="activeTab" class="permissions-tabs" content-class="mt-4" nav-wrapper-class="tabs-nav">
                        <b-tab v-for="(permissions, group) in filteredPermissionsList" :key="group" :title="formatGroupName(group)" :title-link-class="'tab-' + group">
                            <template #title>
                                <span class="tab-title">
                                    {{ formatGroupName(group) }}
                                    <b-badge variant="light" class="ml-2">{{ getGroupPermCount(group) }}</b-badge>
                                </span>
                            </template>

                            <!-- Component Sections -->
                            <div class="components-grid">
                        <template v-for="(permList, component) in permissions">

                            <!-- Multi-schema component: single merged card -->
                            <div
                                v-if="!isSchemaKey(permissions, component) && getSchemaChildren(permissions, component).length > 0"
                                :key="component"
                                class="component-card component-card--multi-schema"
                                :class="{ 'has-selected': hasSelectedPermsOrChildren(group, component) }"
                            >
                                <!-- Card header -->
                                <div class="component-header">
                                    <div class="component-info">
                                        <h6 class="component-name">
                                            {{ formatComponentName(component) }}
                                            <b-badge variant="secondary" class="ml-2 schema-badge">{{ $t('multiSchema') }}</b-badge>
                                        </h6>
                                    </div>
                                </div>

                                <!-- 1. Default section: schema-type perms on base key (covers all schemas) -->
                                <div class="multi-schema-section">
                                    <div class="multi-schema-section-label" @click="toggleAllComponentPerms(group, component, getBaseSchemaPerms(permissions, component, permList))">
                                        <span>
                                            <b-icon icon="arrow-return-right" class="mr-1 schema-specific-icon"></b-icon>
                                            {{ $t('defaultSchema') }}
                                        </span>
                                        <span class="section-count-area">
                                            <span class="component-count">
                                                {{ getSelectedCount(group, component, getBaseSchemaPerms(permissions, component, permList)) }}/{{ getBaseSchemaPerms(permissions, component, permList).length }}
                                                <span class="covers-all-hint">· {{ $t('coversAllSchemas') }}</span>
                                            </span>
                                            <b-form-checkbox
                                                :checked="areAllSelected(group, component, getBaseSchemaPerms(permissions, component, permList))"
                                                :indeterminate="isSomeSelected(group, component, getBaseSchemaPerms(permissions, component, permList))"
                                                @change="toggleAllComponentPerms(group, component, getBaseSchemaPerms(permissions, component, permList))"
                                                class="select-all-checkbox ml-2"
                                            ></b-form-checkbox>
                                        </span>
                                    </div>
                                    <div class="permissions-list">
                                        <div
                                            v-for="perm in getBaseSchemaPerms(permissions, component, permList)"
                                            :key="perm"
                                            class="permission-item"
                                            @click="togglePerm(perm, component)"
                                        >
                                            <b-form-checkbox
                                                :checked="isPermSelected(perm, component)"
                                                @change="togglePerm(perm, component)"
                                                class="permission-checkbox"
                                            >
                                                <span class="permission-label">{{ formatPermissionName(perm) }}</span>
                                            </b-form-checkbox>
                                        </div>
                                    </div>
                                </div>

                                <!-- 2. Schema-specific children as flat sibling sections -->
                                <div
                                    v-for="child in getSchemaChildren(permissions, component)"
                                    :key="child.key"
                                    class="multi-schema-section"
                                >
                                    <div class="multi-schema-section-label" @click="toggleAllComponentPerms(group, child.key, child.permList)">
                                        <span>
                                            <b-icon icon="arrow-return-right" class="mr-1 schema-specific-icon"></b-icon>
                                            {{ formatSchemaName(child.key) }}
                                        </span>
                                        <span class="section-count-area">
                                            <span class="component-count">{{ getSelectedCount(group, child.key, child.permList) }}/{{ child.permList ? child.permList.length : 0 }}</span>
                                            <b-form-checkbox
                                                :checked="areAllSelected(group, child.key, child.permList)"
                                                :indeterminate="isSomeSelected(group, child.key, child.permList)"
                                                @change="toggleAllComponentPerms(group, child.key, child.permList)"
                                                class="select-all-checkbox ml-2"
                                            ></b-form-checkbox>
                                        </span>
                                    </div>
                                    <div class="permissions-list">
                                        <div
                                            v-for="perm in child.permList"
                                            :key="perm"
                                            class="permission-item"
                                            @click="togglePerm(perm, child.key)"
                                        >
                                            <b-form-checkbox
                                                :checked="isPermSelected(perm, child.key)"
                                                @change="togglePerm(perm, child.key)"
                                                class="permission-checkbox"
                                            >
                                                <span class="permission-label">{{ formatPermissionName(perm) }}</span>
                                            </b-form-checkbox>
                                        </div>
                                    </div>
                                </div>

                                <!-- 3. Component-only perms (settings/functions/etc.) last -->
                                <div
                                    v-if="getBaseComponentPerms(permissions, component, permList).length > 0"
                                    class="multi-schema-section"
                                >
                                    <div class="multi-schema-section-label" @click="toggleAllComponentPerms(group, component, getBaseComponentPerms(permissions, component, permList))">
                                        <span>
                                            <b-icon icon="gear" class="mr-1"></b-icon>
                                            {{ $t('componentPermissions') }}
                                        </span>
                                        <span class="section-count-area">
                                            <span class="component-count">{{ getSelectedCount(group, component, getBaseComponentPerms(permissions, component, permList)) }}/{{ getBaseComponentPerms(permissions, component, permList).length }}</span>
                                            <b-form-checkbox
                                                :checked="areAllSelected(group, component, getBaseComponentPerms(permissions, component, permList))"
                                                :indeterminate="isSomeSelected(group, component, getBaseComponentPerms(permissions, component, permList))"
                                                @change="toggleAllComponentPerms(group, component, getBaseComponentPerms(permissions, component, permList))"
                                                class="select-all-checkbox ml-2"
                                            ></b-form-checkbox>
                                        </span>
                                    </div>
                                    <div class="permissions-list">
                                        <div
                                            v-for="perm in getBaseComponentPerms(permissions, component, permList)"
                                            :key="perm"
                                            class="permission-item"
                                            @click="togglePerm(perm, component)"
                                        >
                                            <b-form-checkbox
                                                :checked="isPermSelected(perm, component)"
                                                @change="togglePerm(perm, component)"
                                                class="permission-checkbox"
                                            >
                                                <span class="permission-label">{{ formatPermissionName(perm) }}</span>
                                            </b-form-checkbox>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Regular component (no schema children): single card -->
                            <div
                                v-else-if="!isSchemaKey(permissions, component)"
                                :key="component"
                                class="component-card"
                                :class="{ 'has-selected': hasSelectedPerms(group, component) }"
                            >
                                <div class="component-header" @click="toggleAllComponentPerms(group, component, permList)">
                                    <div class="component-info">
                                        <h6 class="component-name">{{ formatComponentName(component) }}</h6>
                                        <span class="component-count">{{ getSelectedCount(group, component, permList) }}/{{ permList ? permList.length : 0 }}</span>
                                    </div>
                                    <b-form-checkbox
                                        :checked="areAllSelected(group, component, permList)"
                                        :indeterminate="isSomeSelected(group, component, permList)"
                                        @change="toggleAllComponentPerms(group, component, permList)"
                                        class="select-all-checkbox"
                                    ></b-form-checkbox>
                                </div>
                                <div class="permissions-list">
                                    <div
                                        v-for="perm in permList"
                                        :key="perm"
                                        class="permission-item"
                                        @click="togglePerm(perm, component)"
                                    >
                                        <b-form-checkbox
                                            :checked="isPermSelected(perm, component)"
                                            @change="togglePerm(perm, component)"
                                            class="permission-checkbox"
                                        >
                                            <span class="permission-label">{{ formatPermissionName(perm) }}</span>
                                        </b-form-checkbox>
                                    </div>
                                </div>
                            </div>

                        </template>
                    </div>
                </b-tab>
            </b-tabs>
                
                <button 
                    class="scroll-button scroll-right" 
                    @click="scrollTabs('right')"
                    v-show="showRightArrow"
                >
                    <b-icon icon="chevron-right"></b-icon>
                </button>
            </div>

            <!-- Selected Permissions Summary -->
            <div class="selected-permissions" v-if="perms.length > 0">
                <div class="summary-header">
                    <h5>{{ $t('selectedPermissions') }}</h5>
                    <b-button variant="outline-danger" size="sm" @click="clearAllPerms">
                        {{ $t('clearAll') }}
                    </b-button>
                </div>
                <div class="permissions-badges">
                    <b-badge 
                        v-for="perm in perms" 
                        :key="perm"
                        variant="success" 
                        class="perm-badge"
                        @click="removePermFromList(perm)"
                    >
                        {{ perm }}
                        <b-icon icon="x" class="ml-1"></b-icon>
                    </b-badge>
                </div>
            </div>
        </b-card>
        </div>

        <!-- Clone Modal -->
        <main-dialog
            id="clone-role-modal"
            v-model="cloneModal.show"
            :title="$t('cloneRole')"
            @ok="cloneRole"
            @hidden="resetCloneModal"
        >
            <b-form-group :label="$t('newRoleName')" label-for="clone-name">
                <b-form-input
                    id="clone-name"
                    v-model="cloneModal.name"
                    :placeholder="$t('enterRoleName')"
                    autofocus
                ></b-form-input>
            </b-form-group>
            <b-alert show variant="info" class="mt-3">
                <small>{{ $t('cloneRoleDescription') }}</small>
            </b-alert>
        </main-dialog>
    </div>
</template>

<script>
import _ from "lodash"
import VSelect from "vue-select"
import { Switch as cSwitch } from '@coreui/vue'

export default {
    name: "Edit",

    components: {
        VSelect,
        cSwitch,
    },

    props: {
        roleUuid: {
            type: String,
            default: null
        }
    },

    data: function () {
        return {
            name: "",
            level: null,
            perms: [],
            spaces: [],
            tags: [],
            activeTab: 0,
            filter: '',
            loading: true,
            showLeftArrow: false,
            showRightArrow: false,
            cloneModal: {
                show: false,
                name: ''
            }
        }
    },
    computed: {
        uuid() {
            return this.roleUuid || this.$route.params.id
        },

        permissionsList() {
            return this.$store.getters.permissionsList
        },

        forgeAppMap() {
            const apps = this.$store.getters.getAllForgeApps
            const map = {}
            for (const app of apps) {
                map[app.uuid] = app.name || app.label?.locales?.en || app.uuid
            }
            return map
        },

        groups() {
            return Object.keys(this.permissionsList)
        },

        workspaces() {
            return this.$store.getters.getCurrentProject.workspaces.map(w => {
                return {
                    label: w.name,
                    code: w.uuid
                }
            })
        },

        filteredPermissionsList() {
            if (!this.filter) return this.permissionsList
            
            const searchLower = this.filter.toLowerCase()
            const filtered = {}
            
            Object.keys(this.permissionsList).forEach(group => {
                const groupPerms = this.permissionsList[group]
                const filteredGroup = {}
                
                Object.keys(groupPerms).forEach(component => {
                    const componentPerms = groupPerms[component]
                    if (!componentPerms || !Array.isArray(componentPerms)) return
                    const matchingPerms = componentPerms.filter(perm => {
                        const fullPerm = `${perm} ${component}`.toLowerCase()
                        const formattedPerm = this.formatPermissionName(perm).toLowerCase()
                        const formattedComponent = this.formatComponentName(component).toLowerCase()
                        return fullPerm.includes(searchLower) || 
                               formattedPerm.includes(searchLower) || 
                               formattedComponent.includes(searchLower)
                    })
                    
                    if (matchingPerms.length > 0) {
                        filteredGroup[component] = matchingPerms
                    }
                })
                
                if (Object.keys(filteredGroup).length > 0) {
                    filtered[group] = filteredGroup
                }
            })
            
            // Ensure base components are present when only a schema child matched
            Object.keys(filtered).forEach(function(group) {
                var fullGroup = this.permissionsList[group] || {}
                var filteredGroup = filtered[group]
                Object.keys(filteredGroup).forEach(function(component) {
                    var lastDot = component.lastIndexOf('.')
                    if (lastDot !== -1) {
                        var prefix = component.substring(0, lastDot)
                        if (fullGroup[prefix] && !filteredGroup[prefix]) {
                            filteredGroup[prefix] = fullGroup[prefix]
                        }
                    }
                })
            }.bind(this))
            
            return filtered
        },
    },
    methods: {
        formatGroupName(group) {
            return _.startCase(group)
        },

        formatComponentName(val) {
            var satelliteMatch = val.match(/^satellite::(.+)$/i)
            if (satelliteMatch) {
                return _.startCase(satelliteMatch[1].replaceAll("-", " ").replaceAll("_", " "))
            }

            // Forge service: "forge::service::{appUuid}::{serviceName}"
            var serviceMatch = val.match(/^forge::service::([a-f0-9-]+)::(.+)$/i)
            if (serviceMatch) {
                var appName = this.forgeAppMap[serviceMatch[1]] || serviceMatch[1]
                return _.startCase(appName) + ' / ' + _.startCase(serviceMatch[2])
            }

            // Forge app: "forge::{appUuid}"
            var forgeAppMatch = val.match(/^forge::([a-f0-9-]{36})$/i)
            if (forgeAppMatch) {
                return _.startCase(this.forgeAppMap[forgeAppMatch[1]] || forgeAppMatch[1])
            }

            var cleaned = val.replace(/:[0-9a-f-]+$/i, '')
            return _.startCase(cleaned.replaceAll("thunder", "").replaceAll("::", " ").replaceAll("_", " "))
        },

        formatPermissionName(perm) {
            return perm
        },

        formatSchemaName(component) {
            var lastDot = component.lastIndexOf('.')
            if (lastDot !== -1) {
                return component.substring(lastDot + 1)
            }
            return component
        },

        isSchemaKey(permissions, component) {
            var lastDot = component.lastIndexOf('.')
            if (lastDot === -1) return false
            var prefix = component.substring(0, lastDot)
            return Object.prototype.hasOwnProperty.call(permissions, prefix)
        },

        getSchemaChildren(permissions, component) {
            var children = []
            Object.keys(permissions).forEach(function(key) {
                var lastDot = key.lastIndexOf('.')
                if (lastDot !== -1 && key.substring(0, lastDot) === component) {
                    children.push({ key: key, permList: permissions[key] })
                }
            })
            return children
        },

        hasSelectedPermsOrChildren(group, component) {
            if (this.hasSelectedPerms(group, component)) return true
            var groupPerms = this.permissionsList[group] || {}
            var keys = Object.keys(groupPerms)
            for (var i = 0; i < keys.length; i++) {
                var key = keys[i]
                var lastDot = key.lastIndexOf('.')
                if (lastDot !== -1 && key.substring(0, lastDot) === component) {
                    if (this.hasSelectedPerms(group, key)) return true
                }
            }
            return false
        },

        getBaseSchemaPerms(permissions, component, permList) {
            var children = this.getSchemaChildren(permissions, component)
            if (children.length === 0) return permList || []
            var schemaActions = {}
            children.forEach(function(child) {
                if (child.permList) {
                    child.permList.forEach(function(p) { schemaActions[p] = true })
                }
            })
            return (permList || []).filter(function(p) { return schemaActions[p] })
        },

        getBaseComponentPerms(permissions, component, permList) {
            var children = this.getSchemaChildren(permissions, component)
            if (children.length === 0) return permList || []
            var schemaActions = {}
            children.forEach(function(child) {
                if (child.permList) {
                    child.permList.forEach(function(p) { schemaActions[p] = true })
                }
            })
            return (permList || []).filter(function(p) { return !schemaActions[p] })
        },

        hasAnySelectedInList(permList, component) {
            if (!permList || permList.length === 0) return false
            for (var i = 0; i < permList.length; i++) {
                if (this.isPermSelected(permList[i], component)) return true
            }
            return false
        },

        getGroupPermCount(group) {
            const groupPerms = this.permissionsList[group] || {}
            return Object.values(groupPerms).flat().length
        },

        hasSelectedPerms(group, component) {
            const groupPerms = this.permissionsList[group] || {}
            const permList = groupPerms[component] || []
            return permList.some(perm => this.isPermSelected(perm, component))
        },

        getSelectedCount(group, component, permList) {
            if (!permList || !Array.isArray(permList)) return 0
            return permList.filter(perm => this.isPermSelected(perm, component)).length
        },

        isPermSelected(perm, component) {
            return this.perms.indexOf(`${perm} ${component}`) !== -1
        },

        areAllSelected(group, component, permList) {
            if (!permList || !Array.isArray(permList) || permList.length === 0) return false
            return permList.every(perm => this.isPermSelected(perm, component))
        },

        isSomeSelected(group, component, permList) {
            if (!permList || !Array.isArray(permList) || permList.length === 0) return false
            const selected = permList.filter(perm => this.isPermSelected(perm, component)).length
            return selected > 0 && selected < permList.length
        },

        togglePerm(perm, component) {
            const fullPerm = `${perm} ${component}`
            const index = this.perms.indexOf(fullPerm)
            if (index === -1) {
                this.perms.push(fullPerm)
            } else {
                this.perms.splice(index, 1)
            }
        },

        toggleAllComponentPerms(group, component, permList) {
            if (!permList || !Array.isArray(permList)) return
            const allSelected = this.areAllSelected(group, component, permList)
            permList.forEach(perm => {
                const fullPerm = `${perm} ${component}`
                const index = this.perms.indexOf(fullPerm)
                if (allSelected && index !== -1) {
                    this.perms.splice(index, 1)
                } else if (!allSelected && index === -1) {
                    this.perms.push(fullPerm)
                }
            })
        },

        removePermFromList(perm) {
            const index = this.perms.indexOf(perm)
            if (index !== -1) {
                this.perms.splice(index, 1)
            }
        },

        clearAllPerms() {
            this.$mainDialog.msgBoxConfirm(this.$t('confirmClearAllPermissions')).then(confirmed => {
                if (confirmed) {
                    this.perms = []
                }
            })
        },

        selectAllPermissions() {
            const allPerms = []
            Object.keys(this.permissionsList).forEach(group => {
                const groupPerms = this.permissionsList[group]
                Object.keys(groupPerms).forEach(component => {
                    groupPerms[component].forEach(perm => {
                        const fullPerm = `${perm} ${component}`
                        if (!allPerms.includes(fullPerm)) {
                            allPerms.push(fullPerm)
                        }
                    })
                })
            })
            this.perms = allPerms
        },

        save() {
            this.$http.post(`/v1/user/role/edit/${this.uuid}`, { name: this.name, level: this.level, permissions: this.perms, workspaces: this.spaces, tags: this.tags }).then(() => {
                this.$emit('role-updated')

                this.$bvToast.toast(this.$t('success'), {
                    variant: 'success'
                })
            }).catch((err) => {
                this.$bvToast.toast(this.$t('unexpectedError'), {
                    variant: 'danger',
                    title: this.$t('error')
                })

                console.error("Error saving role", err)
            })
        },
        
        remove() {
            this.$mainDialog.msgBoxConfirm(this.$t('confirmDeleteRole'), {
                title: this.$t('confirmDelete'),
                okVariant: 'danger',
                okTitle: this.$t('delete'),
                cancelTitle: this.$t('cancel')
            }).then(confirmed => {
                if (!confirmed) return

                this.$http.delete("/v1/user/role/delete/" + this.uuid).then(() => {
                    this.$router.push({ path: "/admin/iam/roles" })
                }).catch(error => {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }

                    this.$mainDialog.msgBoxOk(error.response.data.message)
                })
            })
        },

        showCloneModal() {
            this.cloneModal.name = `${this.name} (Copy)`
            this.cloneModal.show = true
        },

        resetCloneModal() {
            this.cloneModal.name = ''
        },

        cloneRole() {
            if (!this.cloneModal.name.trim()) {
                this.$bvToast.toast(this.$t('roleNameRequired'), {
                    variant: 'warning',
                    title: this.$t('validation')
                })
                return
            }

            const cloneData = {
                name: this.cloneModal.name,
                level: this.level,
                permissions: [...this.perms],
                workspaces: [...this.spaces],
                tags: [...this.tags]
            }

            this.$http.post('/v1/user/role/create', cloneData).then(resp => {
                this.$bvToast.toast(this.$t('roleClonedSuccessfully'), {
                    variant: 'success',
                    title: this.$t('success')
                })
                this.$router.push({ path: `/admin/iam/roles/edit/${resp.data.uuid}` })
            }).catch(err => {
                const errorMessage = (err.response && err.response.data && err.response.data.message) || this.$t('unexpectedError')
                this.$bvToast.toast(errorMessage, {
                    variant: 'danger',
                    title: this.$t('error')
                })
                console.error('Error cloning role', err)
            })
        },

        tabsScroller() {
            const tabs = this.$refs.permissionsTabs
            const root = tabs && (tabs.$el || tabs)
            return root ? root.querySelector('.tabs-nav') : null
        },

        attachTabsScrollListener() {
            const wrapper = this.tabsScroller()
            if (!wrapper) return null

            if (this._tabsScrollElement !== wrapper) {
                if (this._tabsScrollElement) {
                    this._tabsScrollElement.removeEventListener('scroll', this.handleTabScroll)
                }
                wrapper.addEventListener('scroll', this.handleTabScroll)
                this._tabsScrollElement = wrapper
            }

            return wrapper
        },

        scrollTabs(direction) {
            const wrapper = this.attachTabsScrollListener()
            if (!wrapper) return
            
            const scrollAmount = 200
            const currentScroll = wrapper.scrollLeft
            
            if (direction === 'left') {
                wrapper.scrollTo({
                    left: currentScroll - scrollAmount,
                    behavior: 'smooth'
                })
            } else {
                wrapper.scrollTo({
                    left: currentScroll + scrollAmount,
                    behavior: 'smooth'
                })
            }
        },

        handleTabScroll() {
            const wrapper = this.tabsScroller()
            if (!wrapper) return
            
            this.showLeftArrow = wrapper.scrollLeft > 0
            this.showRightArrow = wrapper.scrollLeft < (wrapper.scrollWidth - wrapper.clientWidth - 1)
        },

        checkTabsOverflow() {
            this.$nextTick(() => {
                const wrapper = this.attachTabsScrollListener()
                if (!wrapper) return
                
                this.showRightArrow = wrapper.scrollWidth > wrapper.clientWidth
                this.handleTabScroll()
            })
        }
    },
    mounted() {
        this.$store.dispatch("fetchPermissionsList")
        this.$store.dispatch("fetchAllForgeApps")

        this.$http.get("/v1/user/role/edit/" + this.uuid).then(resp => {
            this.name = resp.data.name
            this.level = resp.data.level
            this.perms = resp.data.permissions || []
            this.spaces = resp.data.workspaces || []
            this.tags = resp.data.tags || []
            this.loading = false
            this.checkTabsOverflow()
        }).catch(err => {
            console.error("Error fetching role data", err)
            this.loading = false
            
            this.$bvToast.toast(this.$t('unexpectedError'), {
                title: this.$t('error'),
                variant: "danger"
            })
        })
    },
    beforeDestroy() {
        if (this._tabsScrollElement) {
            this._tabsScrollElement.removeEventListener('scroll', this.handleTabScroll)
        }
    }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.role-edit-container {
    max-width: 1400px;
    margin: 0 auto;
}

.role-basic-info {
    h4 {
        color: var(--text-primary);
        font-weight: 600;
    }
}

.permissions-card {
    .permissions-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        h4 {
            color: var(--text-primary);
            font-weight: 600;
            margin: 0;
        }
        
        .header-actions {
            display: flex;
            align-items: center;
            gap: 8px;
            
            .badge {
                font-size: 14px;
                padding: 8px 16px;
            }
        }
    }
}

.permissions-tabs {
    width: 100%;

    ::v-deep .tabs-nav {
        overflow-x: auto;
        overflow-y: hidden;
        padding: 0 44px;
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
    }

    ::v-deep .tabs-nav::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera */
    }

    ::v-deep .nav-tabs {
        border-bottom: 2px solid $dark-border2;
        flex-wrap: nowrap !important;
        
        .nav-link {
            border: none;
            border-bottom: 3px solid transparent;
            color: var(--text-secondary);
            padding: 12px 20px;
            font-weight: 500;
            transition: all 0.2s;
            white-space: nowrap;
            
            &:hover {
                color: var(--text-primary);
                border-bottom-color: rgba(102, 126, 234, 0.5);
            }
            
            &.active {
                background: transparent;
                color: $protokol-royal-active;
                border-bottom-color: $protokol-royal-active;
                
                .badge {
                    color: $protokol-royal-active !important;
                }
            }
        }
    }

    ::v-deep .tab-content {
        overflow: visible;
    }
    
    .tab-title {
        display: flex;
        align-items: center;
        gap: 8px;
    }
}

.tabs-container {
    position: relative;
    display: block;
}

.scroll-button {
    position: absolute;
    top: 6px;
    z-index: 2;
    width: 36px;
    height: 36px;
    border: 1px solid $dark-border2;
    background: $dark-hard2-background;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    color: var(--text-secondary);
    
    &:hover {
        background: $dark-hard-background;
        border-color: $protokol-royal-active;
        color: $protokol-royal-active;
    }
    
    &.scroll-left {
        left: 0;
    }
    
    &.scroll-right {
        right: 0;
    }
}

.components-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 20px;
}

.component-card {
    background: $dark-hard2-background;
    border: 1px solid $dark-border2;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.2s;
    
    &.has-selected {
        border-color: $protokol-royal-active;
        box-shadow: 0 0 0 1px rgba(102, 126, 234, 0.3);
    }
    
    &:hover {
        border-color: $dark-border;
    }

    // The 'All schemas' base card has a subtle tinted header to distinguish it
    .multi-schema-section {
        border-top: 1px solid $dark-border2;

        .multi-schema-section-label {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 16px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.4px;
            color: var(--text-muted);
            cursor: pointer;
            transition: background 0.2s;

            &:hover {
                background: $dark-hard-background;
            }

            .section-count-area {
                display: flex;
                align-items: center;
            }

            .schema-specific-icon {
                color: var(--text-muted);
                font-size: 11px;
            }
        }

        &--children {
            // kept for compatibility
        }
    }

    .schema-badge {
        font-size: 10px;
        font-weight: 500;
        vertical-align: middle;
        padding: 2px 6px;
    }

    .covers-all-hint {
        font-size: 11px;
        color: var(--text-muted);
    }

    // base-component-section kept for backwards compat
    .base-component-section {
        border-top: 1px solid $dark-border2;

        .base-component-section-label {
            padding: 8px 16px 4px;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.4px;
            color: var(--text-muted);
        }
    }
    
    .component-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        background: $dark-background;
        border-bottom: 1px solid $dark-border2;
        cursor: pointer;
        
        &:hover {
            background: $dark-hard-background;
        }
        
        .component-info {
            flex: 1;
            
            .component-name {
                margin: 0;
                font-size: 14px;
                font-weight: 600;
                color: var(--text-primary);

                .schema-specific-icon {
                    color: var(--text-muted);
                    font-size: 11px;
                }
            }
            
            .component-count {
                font-size: 12px;
                color: var(--text-muted);
            }
        }
        
        .select-all-checkbox {
            margin: 0;
        }
    }
    
    .permissions-list {
        padding: 12px;
        
        .permission-item {
            padding: 8px 12px;
            border-radius: 6px;
            cursor: pointer;
            transition: background 0.2s;
            
            &:hover {
                background: $dark-background;
            }
            
            .permission-checkbox {
                margin: 0;
                
                .permission-label {
                    font-size: 13px;
                    color: var(--text-primary);
                }
            }
        }
    }
}

.selected-permissions {
    margin-top: 32px;
    padding: 24px;
    background: $dark-hard2-background;
    border-radius: 12px;
    border: 1px solid $dark-border2;
    
    .summary-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        
        h5 {
            margin: 0;
            color: var(--text-primary);
            font-weight: 600;
        }
    }
    
    .permissions-badges {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        
        .perm-badge {
            padding: 8px 12px;
            font-size: 12px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            display: inline-flex;
            align-items: center;
            
            &:hover {
                background: #dc3545;
                transform: scale(1.05);
            }
        }
    }
}

::v-deep .custom-control-label {
    cursor: pointer;
}

.loading-state {
    .component-card-skeleton {
        background: $dark-hard2-background;
        border: 1px solid $dark-border2;
        border-radius: 12px;
        padding: 16px;
    }
}
</style>
